/**
 * Activation entry point for the customer settings page.
 *
 * A capability-gated affordance for taking a customer out of service and for
 * bringing one back: it renders nothing unless the acting user holds the
 * deactivate-customer capability, and otherwise offers whichever of the two
 * the customer's current state calls for. This is the only place a customer
 * can be deactivated or reactivated — there is no grid bulk action.
 *
 * The two are not mirror images. Deactivation is a destructive background
 * cascade over the customer's groups, sites and radios, so it goes through a
 * wizard. Reactivation only clears the customer's own flag, so it is a
 * confirm, and the copy has to say what it leaves behind. Both sit in the
 * settings pages' shared Danger Zone card.
 *
 * Contains:
 * - CustomerDeactivatePanel — the gated trigger + dialog host (default export)
 */

import { Alert, Button, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCustomer, reactivateCustomer } from '@sophocles/api/customer';
import ConfirmationDialog from '@sophocles/components/ConfirmationDialog/ConfirmationDialog';
import { loadCapabilities, selectCapabilities } from '@sophocles/features/capabilities';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { DANGER_ZONE_CONTROL_WIDTH, SettingsDangerZone, SettingsFieldRow } from '@sophocles/views/settings/components';

import CustomerDeactivateDialog from './CustomerDeactivateDialog';

export default function CustomerDeactivatePanel({ customerId, customerName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const capabilities = useSelector(selectCapabilities);
  const [open, setOpen] = React.useState(false);
  const [isConfirmingReactivate, setIsConfirmingReactivate] = React.useState(false);
  const [isReactivating, setIsReactivating] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState([]);
  // null until the customer's state is known: which of the two affordances
  // belongs here is the whole question this panel answers, so it offers
  // neither rather than guessing and offering the wrong one.
  const [isDeactivated, setIsDeactivated] = React.useState(null);

  // Capabilities are seeded on the site dashboard; the settings routes don't
  // pass through it, so ensure they're loaded here. The thunk is conditional
  // and idempotent — a no-op once loaded.
  React.useEffect(() => {
    dispatch(loadCapabilities());
  }, [dispatch]);

  // The settings document does not carry `deactivated`: the flag moves
  // through the deactivate and reactivate actions, never through a settings
  // PATCH, so it is read from the customer record itself.
  React.useEffect(() => {
    let isActive = true;
    fetchCustomer(customerId)
      .then(customer => {
        if (isActive) setIsDeactivated(Boolean(customer?.deactivated));
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [customerId]);

  const handleReactivate = async () => {
    setIsConfirmingReactivate(false);
    setIsReactivating(true);
    setErrorMessages([]);
    try {
      await reactivateCustomer(customerId);
      setIsDeactivated(false);
      dispatch(
        addToast({
          message: t('customerDeactivate.reactivateSuccess', '{{name}} has been reactivated.', {
            name: customerName ?? '',
          }),
          level: 'success',
        }),
      );
    } catch (error) {
      setErrorMessages(parseApiError(error).nonFieldMessages);
    } finally {
      setIsReactivating(false);
    }
  };

  if (!capabilities?.can_deactivate_customer || isDeactivated === null) {
    return null;
  }

  const chip = isDeactivated ? (
    <Chip size="small" variant="outlined" label={t('customerDeactivate.deactivatedNow', 'Currently deactivated')} />
  ) : (
    <Chip
      size="small"
      variant="outlined"
      color="error"
      label={t('customerDeactivate.requiresPermission', 'Requires the Deactivate Customer permission')}
    />
  );

  return (
    <>
      <SettingsDangerZone
        chip={chip}
        description={t(
          'customerDeactivate.dangerZoneIntro',
          'Deactivate this customer, or reactivate one that was deactivated.',
        )}
      >
        <SettingsFieldRow
          label={
            isDeactivated
              ? t('customerDeactivate.reactivateTitle', 'Reactivate customer')
              : t('customerDeactivate.dangerZoneTitle', 'Deactivate customer')
          }
          description={
            isDeactivated
              ? t(
                  'customerDeactivate.reactivateDescription',
                  'Puts this customer back in service. Its groups, sites and units stay as the deactivation left them — bring those back from their own pages.',
                )
              : t(
                  'customerDeactivate.dangerZoneDescription',
                  'Permanently deactivates this customer and all of its active sites.',
                )
          }
          controlWidth={DANGER_ZONE_CONTROL_WIDTH}
        >
          {isDeactivated ? (
            <Button
              variant="outlined"
              color="success"
              onClick={() => setIsConfirmingReactivate(true)}
              disabled={isReactivating}
            >
              {t('customerDeactivate.reactivateTrigger', 'Reactivate customer…')}
            </Button>
          ) : (
            <Button variant="contained" color="error" onClick={() => setOpen(true)}>
              {t('customerDeactivate.trigger', 'Deactivate customer…')}
            </Button>
          )}
        </SettingsFieldRow>
        {errorMessages.length > 0 && (
          <Alert severity="error" sx={{ my: 1 }} onClose={() => setErrorMessages([])}>
            {errorMessages.join(' ')}
          </Alert>
        )}
      </SettingsDangerZone>
      <ConfirmationDialog
        open={isConfirmingReactivate}
        title={t('customerDeactivate.reactivateConfirm', 'Reactivate {{name}}?', { name: customerName ?? '' })}
        submit={handleReactivate}
        closeDialog={() => setIsConfirmingReactivate(false)}
        submitLabel={t('actions.reactivate', 'Reactivate')}
        cancelLabel={t('actions.cancel', 'Cancel')}
        submitDisabled={isReactivating}
      >
        {t(
          'customerDeactivate.reactivateConfirmBody',
          'Only the customer is reactivated. Its groups, sites and units are not.',
        )}
      </ConfirmationDialog>
      <CustomerDeactivateDialog
        open={open}
        onClose={() => setOpen(false)}
        customerId={customerId}
        customerName={customerName}
      />
    </>
  );
}

CustomerDeactivatePanel.propTypes = {
  customerId: PropTypes.number.isRequired,
  customerName: PropTypes.string,
};
